import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDeleteKnowledge, useKnowledgeList } from '@/hooks/useKnowledge';
import { extractApiError } from '@/lib/api';
import { friendlyError } from '@/lib/error-messages';
import type { KnowledgeObject } from '@/lib/knowledge';
import { Lightbulb, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function summarisePayload(k: KnowledgeObject): string {
  const parts: string[] = [];
  const f = k.payload?.edges_forbidden?.length ?? 0;
  const r = k.payload?.edges_required?.length ?? 0;
  const t = k.payload?.tiers?.length ?? 0;
  if (f) parts.push(`${f} forbidden`);
  if (r) parts.push(`${r} required`);
  if (t) parts.push(`${t} tier${t === 1 ? '' : 's'}`);
  return parts.join(' · ') || '—';
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-dashed py-16 text-center">
      <Lightbulb className="mb-3 h-10 w-10 text-muted-foreground" />
      <h3 className="text-base font-medium">No knowledge yet</h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        Encode forbidden edges, required edges, or variable tiers — the algorithm uses these to
        refine the graph.
      </p>
      <Button asChild className="mt-4">
        <Link to="/knowledge/new">
          <Plus className="mr-1 h-4 w-4" /> New knowledge
        </Link>
      </Button>
    </div>
  );
}

export default function Knowledge() {
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [accumulated, setAccumulated] = React.useState<KnowledgeObject[]>([]);
  const [pendingDelete, setPendingDelete] = React.useState<KnowledgeObject | null>(null);

  const list = useKnowledgeList({ cursor, limit: 20 });
  const del = useDeleteKnowledge();

  // Same accumulator pattern as DatasetTable — page 1 renders query data
  // directly so a mutation invalidation shows up immediately; subsequent
  // pages append to local state.
  React.useEffect(() => {
    if (cursor === undefined || !list.data) return;
    setAccumulated((prev) => {
      const seen = new Set(prev.map((k) => k.id));
      return [...prev, ...list.data.items.filter((k) => !seen.has(k.id))];
    });
  }, [list.data, cursor]);

  const items = cursor === undefined ? (list.data?.items ?? []) : accumulated;

  function confirmDelete() {
    if (!pendingDelete) return;
    const target = pendingDelete;
    del.mutate(target.id, {
      onSuccess: () => {
        setAccumulated((prev) => prev.filter((k) => k.id !== target.id));
        toast.success(`Deleted "${target.name}"`);
        setPendingDelete(null);
      },
      onError: (err) => {
        const apiErr = extractApiError(err);
        toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Delete failed.'));
        setPendingDelete(null);
      },
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="max-w-2xl space-y-3">
          <h1 className="text-2xl font-semibold tracking-tight">Knowledge</h1>
          <p className="text-sm text-muted-foreground">
            Domain constraints attached to a discovery so the algorithm doesn't have to learn
            everything from data alone. Three kinds, weakest to strongest:
          </p>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            <li>
              <span className="font-medium text-foreground">Tiers</span> — order variables into
              causal layers (upstream → downstream); the algorithm won't draw an arrow pointing
              backwards up the tiers. Strongest signal — on the SACHS benchmark, tiers took directed
              accuracy from ~0% to 100%.
            </li>
            <li>
              <span className="font-medium text-foreground">Forbidden edges</span> — variable pairs
              you know can't be directly connected; never drawn.
            </li>
            <li>
              <span className="font-medium text-foreground">Required edges</span> — relationships
              you know exist; kept even when the data is weak.
            </li>
          </ul>
        </div>
        <Button asChild>
          <Link to="/knowledge/new">
            <Plus className="mr-1 h-4 w-4" /> New knowledge
          </Link>
        </Button>
      </div>

      {list.isLoading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading knowledge…
        </div>
      ) : list.isError ? (
        <div className="rounded-md border bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load knowledge — please refresh.
        </div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Kinds</TableHead>
                  <TableHead>Contents</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell className="font-medium">
                      <div className="truncate">{k.name}</div>
                      <div className="text-xs text-muted-foreground">{k.id}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {k.kinds.map((kind) => (
                          <Badge key={kind} variant="secondary">
                            {kind}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {summarisePayload(k)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {k.dataset_id ? (
                        <span className="font-mono text-xs">{k.dataset_id}</span>
                      ) : (
                        'Org-wide'
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {fmtDate(k.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          asChild
                          variant="ghost"
                          size="icon"
                          aria-label={`Edit ${k.name}`}
                        >
                          <Link to={`/knowledge/${k.id}/edit`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Delete ${k.name}`}
                          onClick={() => setPendingDelete(k)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {list.data?.next_cursor ? (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={list.isFetching}
                onClick={() => setCursor(list.data?.next_cursor ?? undefined)}
              >
                {list.isFetching ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </div>
      )}

      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete knowledge?</DialogTitle>
            <DialogDescription>
              "{pendingDelete?.name}" will be removed. Discoveries that already used it stay intact.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDelete(null)}
              disabled={del.isPending}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={del.isPending}>
              {del.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
